import { LibraryCatalogKind } from '../libraryCatalog';
import { LibraryIntent, SlideObject, VisibleAsset } from '../types';
import { coverCropInsets } from './objectTransform';
import { isFillableShape } from './shapeFrame';

export type PlaceFit = 'contain' | 'cover';

export function shouldContainPlacedMedia(input: {
  selected?: Pick<SlideObject, 'type'> | null;
  intent?: LibraryIntent;
  asset?: Pick<VisibleAsset, 'articleNumber'> | null;
  catalogKind?: LibraryCatalogKind | null;
}): boolean {
  if (input.catalogKind === 'product' || input.catalogKind === 'wine') return true;
  if (input.selected?.type === 'product') return true;
  if (input.intent === 'insert-product') return true;
  return Boolean(String(input.asset?.articleNumber || '').trim());
}

export function fitImageInFrame(object: SlideObject): SlideObject {
  return {
    ...object,
    style: {
      ...object.style,
      objectFit: 'contain',
      cropLeft: 0,
      cropTop: 0,
      cropRight: 0,
      cropBottom: 0,
    },
  };
}

export type PlaceAction =
  | { mode: 'insert'; type: 'image' | 'product' }
  | { mode: 'fill'; type: 'image' | 'product' | 'shape' };

export function resolvePlaceAction(
  selected: SlideObject | null | undefined,
  intent: LibraryIntent = 'insert-image',
  options?: { forceInsert?: boolean },
): PlaceAction {
  if (options?.forceInsert || !selected) {
    return { mode: 'insert', type: intent === 'insert-product' ? 'product' : 'image' };
  }
  if (isFillableShape(selected) && intent !== 'insert-product') {
    return { mode: 'fill', type: 'shape' };
  }
  if (selected.type === 'image' || selected.type === 'product') {
    return {
      mode: 'fill',
      type: intent === 'insert-product' || selected.type === 'product' ? 'product' : 'image',
    };
  }
  return { mode: 'insert', type: intent === 'insert-product' ? 'product' : 'image' };
}

export function filledMediaFromUrl(
  object: SlideObject,
  placed: { imageUrl: string; width?: number; height?: number },
): SlideObject {
  return {
    ...applyPlacedMedia(object, placed, { preserveFrame: true, fit: 'contain' }),
    assetId: null,
    assetVersionId: null,
    trackingMode: object.trackingMode || 'pin',
  };
}

export function filledShapeFromUrl(object: SlideObject, imageUrl: string): SlideObject {
  return {
    ...object,
    type: 'shape',
    assetId: null,
    assetVersionId: null,
    imageUrl,
    style: {
      ...object.style,
      objectFit: 'cover',
      cropLeft: 0,
      cropTop: 0,
      cropRight: 0,
      cropBottom: 0,
    },
  };
}

export function filledShapeObject(object: SlideObject, asset: Pick<VisibleAsset, '_id' | 'currentVersionId' | 'previewUrl'>): SlideObject {
  return {
    ...object,
    type: 'shape',
    assetId: asset._id,
    assetVersionId: asset.currentVersionId || null,
    trackingMode: object.trackingMode || 'pin',
    imageUrl: asset.previewUrl || object.imageUrl || '',
    style: {
      ...object.style,
      objectFit: 'cover',
      cropLeft: 0,
      cropTop: 0,
      cropRight: 0,
      cropBottom: 0,
    },
  };
}

export function applyPlacedMedia(
  object: SlideObject,
  placed: { imageUrl: string; width?: number; height?: number; assetVersionId?: string },
  options?: { preserveFrame?: boolean; fit?: PlaceFit },
): SlideObject {
  const next: SlideObject = {
    ...object,
    imageUrl: placed.imageUrl,
    assetVersionId: placed.assetVersionId || object.assetVersionId,
  };
  if (options?.preserveFrame && object.type === 'shape') {
    return { ...next, style: { ...object.style, objectFit: object.style?.objectFit || 'cover' } };
  }
  if (options?.preserveFrame && options.fit !== 'contain' && placed.width && placed.height) {
    return {
      ...next,
      style: {
        ...object.style,
        objectFit: 'contain',
        ...coverCropInsets(object.w, object.h, placed.width, placed.height),
      },
    };
  }
  if (options?.preserveFrame) {
    return {
      ...next,
      style: {
        ...object.style,
        objectFit: 'contain',
        cropLeft: 0,
        cropTop: 0,
        cropRight: 0,
        cropBottom: 0,
      },
    };
  }
  return {
    ...next,
    style: { ...object.style, objectFit: object.style?.objectFit || 'contain' },
  };
}

export function clearedShapeMedia(object: SlideObject): SlideObject {
  return {
    ...object,
    assetId: null,
    assetVersionId: null,
    imageUrl: '',
    style: {
      ...object.style,
      cropLeft: 0,
      cropTop: 0,
      cropRight: 0,
      cropBottom: 0,
    },
  };
}
